import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Edit3, Plus, Save, Trash2, X } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { deleteResource, getResource, saveResource } from "../../lib/api";
import type {
  AdminResource,
  ContentItem,
  NavigationItem,
  SocialLink,
  Statistic,
} from "../../types/content";
import { AdminHeader, ConfirmDialog, EmptyState, Field, Notice, Toggle } from "./AdminUi";

type Item = ContentItem | Statistic | NavigationItem | SocialLink;

const meta: Record<AdminResource, { title: string; description: string; singular: string }> = {
  services: { title: "Capabilities", description: "What Steria House can design, build, and improve.", singular: "capability" },
  principles: { title: "Principles", description: "The ideas that guide how Steria House builds products.", singular: "principle" },
  statistics: { title: "Statistics", description: "Dynamic metrics and signals of progress.", singular: "statistic" },
  "social-links": { title: "Social links", description: "Public social profiles shown in the website footer.", singular: "social link" },
  navigation: { title: "Navigation", description: "Labels and destinations in the public website navigation.", singular: "navigation item" },
};

function emptyItem(resource: AdminResource, order: number): Item {
  const base = { id: "", displayOrder: order, isActive: true };
  if (resource === "statistics") return { ...base, value: "", label: "", description: "" } as Statistic;
  if (resource === "navigation") return { ...base, label: "", url: "" } as NavigationItem;
  if (resource === "social-links") return { ...base, platform: "", url: "", icon: "link" } as SocialLink;
  return { ...base, title: "", description: "", icon: "sparkles" } as ContentItem;
}

export function ResourceManager({ resource }: { resource: AdminResource }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["admin-resource", resource], queryFn: () => getResource<Item>(resource) });
  const [editing, setEditing] = useState<Item | null>(null);
  const [deleting, setDeleting] = useState<Item | null>(null);
  const [notice, setNotice] = useState("");
  const items = useMemo(
    () => [...(query.data || [])].sort((a, b) => a.displayOrder - b.displayOrder),
    [query.data],
  );

  const saveMutation = useMutation({
    mutationFn: (item: Item) => saveResource(resource, item),
    onSuccess: () => {
      setEditing(null);
      setNotice(`${meta[resource].singular.replace(/^./, (letter) => letter.toUpperCase())} saved.`);
      queryClient.invalidateQueries({ queryKey: ["admin-resource", resource] });
      queryClient.invalidateQueries({ queryKey: ["public-landing"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteResource(resource, id),
    onSuccess: () => {
      setDeleting(null);
      setNotice("Item deleted.");
      queryClient.invalidateQueries({ queryKey: ["admin-resource", resource] });
      queryClient.invalidateQueries({ queryKey: ["public-landing"] });
    },
  });

  function patch(values: Record<string, unknown>) {
    setEditing((current) => current ? { ...current, ...values } as Item : current);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (editing) saveMutation.mutate(editing);
  }

  async function move(item: Item, direction: number) {
    const index = items.findIndex((candidate) => candidate.id === item.id);
    const other = items[index + direction];
    if (!other) return;
    await Promise.all([
      saveResource(resource, { ...item, displayOrder: other.displayOrder }),
      saveResource(resource, { ...other, displayOrder: item.displayOrder }),
    ]);
    queryClient.invalidateQueries({ queryKey: ["admin-resource", resource] });
    queryClient.invalidateQueries({ queryKey: ["public-landing"] });
  }

  return (
    <>
      <AdminHeader
        title={meta[resource].title}
        description={meta[resource].description}
        action={
          <button className="button button-primary" onClick={() => setEditing(emptyItem(resource, items.length + 1))}>
            Add {meta[resource].singular} <Plus size={17} />
          </button>
        }
      />
      {notice && <Notice type="success" message={notice} onClose={() => setNotice("")} />}
      <section className="admin-card resource-card">
        {!items.length ? (
          <EmptyState
            title={`No ${meta[resource].title.toLowerCase()} yet`}
            description={`Add the first ${meta[resource].singular} to show it on the public site.`}
          />
        ) : (
          <div className="resource-list">
            {items.map((item, index) => (
              <article key={item.id}>
                <div className="resource-order">
                  <button onClick={() => move(item, -1)} disabled={index === 0}><ArrowUp size={14} /></button>
                  <span>{String(item.displayOrder).padStart(2, "0")}</span>
                  <button onClick={() => move(item, 1)} disabled={index === items.length - 1}><ArrowDown size={14} /></button>
                </div>
                <div className="resource-copy">
                  <strong>
                    {"title" in item ? item.title : "label" in item ? item.label : "platform" in item ? item.platform : ""}
                  </strong>
                  <p>
                    {"value" in item
                      ? `${item.value} · ${item.description}`
                      : "description" in item
                        ? item.description
                        : "url" in item
                          ? item.url
                          : ""}
                  </p>
                </div>
                <i className={item.isActive ? "status-active" : "status-inactive"}>{item.isActive ? "Active" : "Hidden"}</i>
                <div className="table-actions">
                  <button onClick={() => setEditing(item)}><Edit3 size={16} /></button>
                  <button onClick={() => setDeleting(item)}><Trash2 size={16} /></button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      {editing && (
        <div className="drawer-backdrop">
          <form className="resource-drawer" onSubmit={submit}>
            <div className="drawer-head">
              <div><h2>{editing.id ? "Edit" : "Add"} {meta[resource].singular}</h2><p>Changes publish to the public website.</p></div>
              <button type="button" onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="drawer-body">
              {(resource === "services" || resource === "principles") && "title" in editing && (
                <>
                  <Field label="Title"><input required value={editing.title} onChange={(event) => patch({ title: event.target.value })} /></Field>
                  <Field label="Description"><textarea required rows={5} value={editing.description} onChange={(event) => patch({ description: event.target.value })} /></Field>
                  <Field label="Icon">
                    <select value={editing.icon} onChange={(event) => patch({ icon: event.target.value })}>
                      <option value="code">Software development</option>
                      <option value="brain">AI & intelligence</option>
                      <option value="education">Education</option>
                      <option value="wellbeing">Wellbeing</option>
                      <option value="compass">Personal growth</option>
                      <option value="design">Product design</option>
                      <option value="sparkles">General</option>
                    </select>
                  </Field>
                </>
              )}
              {resource === "statistics" && "value" in editing && (
                <>
                  <Field label="Value"><input required value={editing.value} onChange={(event) => patch({ value: event.target.value })} /></Field>
                  <Field label="Label"><input required value={editing.label} onChange={(event) => patch({ label: event.target.value })} /></Field>
                  <Field label="Description"><textarea rows={4} value={editing.description} onChange={(event) => patch({ description: event.target.value })} /></Field>
                </>
              )}
              {resource === "navigation" && "label" in editing && "url" in editing && (
                <>
                  <Field label="Label"><input required value={editing.label} onChange={(event) => patch({ label: event.target.value })} /></Field>
                  <Field label="URL"><input required value={editing.url} onChange={(event) => patch({ url: event.target.value })} /></Field>
                </>
              )}
              {resource === "social-links" && "platform" in editing && (
                <>
                  <Field label="Platform"><input required value={editing.platform} onChange={(event) => patch({ platform: event.target.value })} /></Field>
                  <Field label="Profile URL"><input type="url" required value={editing.url} onChange={(event) => patch({ url: event.target.value })} /></Field>
                  <Field label="Icon name"><input value={editing.icon} onChange={(event) => patch({ icon: event.target.value })} /></Field>
                </>
              )}
              <Field label="Display order"><input type="number" min={1} value={editing.displayOrder} onChange={(event) => patch({ displayOrder: Number(event.target.value) })} /></Field>
              <Toggle checked={editing.isActive} onChange={(checked) => patch({ isActive: checked })} label="Active and public" />
            </div>
            <div className="drawer-foot">
              <button type="button" className="button button-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="button button-primary" type="submit" disabled={saveMutation.isPending}>Save <Save size={16} /></button>
            </div>
          </form>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(deleting)}
        title={`Delete this ${meta[resource].singular}?`}
        description="This item will be removed from the CMS and public website."
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
      />
    </>
  );
}
